import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import API from '../utils/api';
import { getBotPage, getGuideReply } from '../utils/botGuide';

const BotContext = createContext(null);
// Public assistant requests must not trigger the account refresh interceptor.
const botAPI = axios.create({ baseURL: API.defaults.baseURL, timeout: 35000 });
const STORAGE_KEY = 'thiranx-bot-chat-v1';
const welcome = () => [{ id: 'welcome', role: 'assistant', text: 'Hey, I’m ThiranX Bot. A course to choose, a concept to untangle, a project to start — let’s take the next step together.', mode: 'guide' }];
function restoreChat() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (Array.isArray(saved) && saved.length && saved.length <= 60 && saved.every(m => ['user', 'assistant'].includes(m.role) && typeof m.text === 'string' && m.text.length <= 12000)) return saved;
  } catch (_) { /* Storage may be unavailable in private browsers. */ }
  return welcome();
}

export function BotProvider({ children }) {
  const { pathname } = useLocation();
  const page = getBotPage(pathname);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(restoreChat);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('checking');
  const [notice, setNotice] = useState('');
  const pending = useRef(false);
  const controller = useRef(null);
  const generation = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    botAPI.get('/ai/status').then(({ data }) => { if (mounted.current) setMode(data.enabled ? 'ai' : 'guide'); }).catch(() => { if (mounted.current) setMode('guide'); });
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-60))); } catch (_) { /* Keep working without storage. */ }
  }, [messages]);
  useEffect(() => { setOpen(false); }, [pathname]);

  const clear = () => {
    generation.current += 1;
    controller.current?.abort();
    pending.current = false;
    setBusy(false);
    setNotice('');
    setMessages(welcome());
  };

  const send = async (value) => {
    const text = value.trim().slice(0, 2000);
    if (!text || pending.current) return;
    pending.current = true;
    const turn = ++generation.current;
    setBusy(true);
    setNotice('');
    const history = messages.filter(m => m.id !== 'welcome').slice(-10).map(m => ({ role: m.role, content: m.text.slice(0, 4000) }));
    setMessages(previous => [...previous, { id: `${turn}-user-${Date.now()}`, role: 'user', text }].slice(-59));
    let answer;
    try {
      if (mode === 'guide') {
        answer = getGuideReply(text, page);
      } else {
        controller.current = new AbortController();
        const { data } = await botAPI.post('/ai/chat', { message: text, history, page: page.name }, { signal: controller.current.signal });
        if (!data.text || typeof data.text !== 'string') throw new Error('Empty reply');
        answer = data;
        if (data.mode === 'guide') { answer = getGuideReply(text, page); setMode('guide'); }
      }
    } catch (error) {
      if (turn !== generation.current || !mounted.current) return;
      answer = getGuideReply(text, page);
      setNotice(error.response?.status === 429 ? 'That’s a few requests in a short time. Here’s a guide answer; try live AI again shortly.' : 'Live AI is unavailable right now. Here’s a built-in guide answer.');
    } finally {
      if (turn === generation.current && mounted.current) { pending.current = false; setBusy(false); }
    }
    if (turn === generation.current && mounted.current && answer) {
      setMessages(previous => [...previous, { ...answer, id: `${turn}-bot-${Date.now()}`, role: 'assistant' }].slice(-60));
    }
  };

  return <BotContext.Provider value={{ open, setOpen, messages, busy, mode, notice, page, send, clear }}>{children}</BotContext.Provider>;
}

export const useBot = () => useContext(BotContext);
